import {useEffect, useRef} from 'react';
import {useCanvas} from '~/common/canvas/hooks/useCanvas';
import {initAligningGuidelines} from './controls/align_lines';
import {useOutsideAlerter} from '~/CreateFabricInvitation/routes/EventCreateDesign/hooks/useOutsideAlerter';
import {useBorderControl} from '~/common/canvas/hooks/useBorderControl';
import {FABRIC_GROUP_START, FABRIC_GROUP_END} from './constants';

export const Canvas = ({
  configJson,
  onChange,
  setCanvas: setCanvasElem,
  setActiveObjects,
  onLoad,
  saveSnapshot,
  className = '',
  parentNode = '',
  readOnly = false,
  smallerScaleRatio = null,
}) => {
  const canvasEl = useRef(null);
  const canvasRef = useRef();
  const canvasWrapperRef = useRef(null);
  const onChangeRef = useRef();
  onChangeRef.current = onChange;

  const onClickOutsideCanvas = () => {
    const canvasInstance = canvasRef.current;
    if (canvasInstance) {
      const activeObject = canvasInstance?.getActiveObject();
      activeObject?.exitEditing?.();
      canvasInstance?.renderAll();
    }
  };

  useOutsideAlerter(canvasWrapperRef, onClickOutsideCanvas);

  const saveCanvas = (canvasInstance) => {
    setCanvasElem(canvasInstance);
    canvasRef.current = canvasInstance;
  };

  useEffect(() => {
    let canvasInstance = {};
    let setCurrentDimensions = () => {};
    const handleResize = () => {
      if (canvasWrapperRef.current && canvasWrapperRef.current.offsetWidth > 0) {
        requestAnimationFrame(() => {
          setCurrentDimensions(parentNode, canvasWrapperRef);
        });
      }
    };

    window.addEventListener('resize', handleResize);

    const c = useCanvas(
      canvasEl.current,
      configJson,
      () => {
        onLoad(canvasInstance);
        handleResize();
      },
      680,
      readOnly,
      smallerScaleRatio
    );
    const {handleBorderLimits} = useBorderControl();
    canvasInstance = c.canvasInstance;
    setCurrentDimensions = c.setCurrentDimensions;
    canvasEl.current.fabric = canvasInstance;

    initAligningGuidelines(canvasInstance);

    // used for automatic tests
    window.canvasElementSelector = (index = 1) => {
      const textboxes = canvasInstance.getObjects().filter((obj) => obj.type === 'textbox');
      if (textboxes.length > 0) {
        canvasInstance.setActiveObject(textboxes[textboxes.length - index]);
        canvasInstance.renderAll();
      }
      return textboxes.length;
    };

    let groupCount = 0;
    const onChangeHandler = () => {
      if (groupCount > 0) {
        return;
      }
      const objectTextFilter = (object) => object.type === 'textbox';
      if (!canvasInstance.isEmpty()) {
        const fabricJson = canvasInstance.toJSON([
          'id',
          '_originalObject',
          'letterCasing',
          '_originalText',
        ]);
        onChangeRef.current({...fabricJson}, configJson.objects.filter(objectTextFilter).length);
      }
    };

    const incrGroupCount = () => {
      groupCount += 1;
    };
    const decrGroupCount = () => {
      groupCount -= 1;
      onChangeHandler();
    };

    const onSelectionEvent = () => {
      setActiveObjects(canvasInstance.getActiveObjects());
    };

    const onDeselectionEvent = () => {
      setActiveObjects(null);
    };

    canvasInstance.on({
      'object:modified': onChangeHandler,
      'object:added': onChangeHandler,
      'object:removed': onChangeHandler,
      'selection:updated': onSelectionEvent,
      'selection:created': onSelectionEvent,
      'selection:cleared': onDeselectionEvent,
      'mouse:up': handleBorderLimits,
      [FABRIC_GROUP_START]: incrGroupCount,
      [FABRIC_GROUP_END]: decrGroupCount,
    });

    saveCanvas(canvasInstance);

    return () => {
      if (saveSnapshot) {
        const snapshot = canvasInstance.toDataURL({
          enableRetinaScaling: true,
        });
        saveSnapshot(snapshot);
      }

      canvasInstance.dispose();
      Raven.setTagsContext({key: 'diposeTime', value: new Date()});
      window.removeEventListener('resize', handleResize);
    };
  }, [readOnly, smallerScaleRatio]);

  return (
    <div ref={canvasWrapperRef} className={className}>
      <canvas id="fabric-canvas" ref={canvasEl} style={readOnly ? {pointerEvents: 'none'} : null} />
    </div>
  );
};
